import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Normalize a list of holiday objects coming from configuration.
 * Expected shape per item: { date: 'YYYY-MM-DD', title?, description? }
 */
export const getHolidayListFromConfig = (configuration) => {
  const items = configuration?.holidays?.items;
  if (!Array.isArray(items)) return [];
  return items.filter((h) => h && h.date);
};

/**
 * Check if the given Dayjs `date` falls on a holiday.
 */
export const isHolidayDate = (date, holidays = [], targetTimezone) => {
  if (
    !date ||
    !date.isValid() ||
    !Array.isArray(holidays) ||
    !holidays.length
  ) {
    return false;
  }

  // Treat holidays as pure calendar dates without timezone shifts.
  // We only compare the YYYY-MM-DD portion so that configured
  // holidays like "2026-02-26" or full ISO strings such as
  // "2026-02-26T00:00:00Z" always map to the same calendar day
  // the user expects, regardless of the local or target timezone.
  const candidate = date.format("YYYY-MM-DD");

  return holidays.some((h) => {
    const holidayDateStr = typeof h === "string" ? h : h.date;
    if (!holidayDateStr) return false;
    // Normalize to just the date part; if the string includes
    // a time (e.g. ISO 8601), slice the first 10 chars.
    const normalizedHoliday = holidayDateStr.slice(0, 10);
    return normalizedHoliday === candidate;
  });
};

/**
 * Factory for a MUI `shouldDisableDate` callback that disables holidays.
 */
export const createHolidayShouldDisableDate = (holidays, targetTimezone) => {
  return (date) => isHolidayDate(date, holidays, targetTimezone);
};

export const getHolidayInfo = ({ date, holidays, targetTimezone }) => {
  if (
    !date ||
    !date.isValid() ||
    !Array.isArray(holidays) ||
    holidays.length === 0
  ) {
    return null;
  }

  // Same calendar-only comparison logic as `isHolidayDate`.
  const candidate = dayjs(date).format("YYYY-MM-DD");

  const match = holidays.find((h) => {
    const holidayDateStr = typeof h === "string" ? h : h?.date;
    if (!holidayDateStr) return false;
    const normalizedHoliday = holidayDateStr.slice(0, 10);
    return normalizedHoliday === candidate;
  });

  return match || null;
};

// Helper: check if a date is a configured holiday
export const isHoliday = ({ date, holidays, targetTimezone }) => {
  return !!getHolidayInfo({ date, holidays, targetTimezone });
};

export const getDateDisableReason = ({ date, holidays, targetTimezone }) => {
  if (!date || !date.isValid()) return "";

  // Treat the incoming value as a plain calendar day to avoid
  // timezone-related off‑by‑one issues (e.g. a Monday being
  // interpreted as Sunday in another timezone).
  const candidate = dayjs(date).startOf("day");
  const today = dayjs().startOf("day");

  // Dates disabled by the picker via `disablePast`
  if (candidate.isBefore(today)) {
    return "Old dates are not selectable";
  }

  // First, check if this date is a configured holiday.
  // `getHolidayInfo` already does a pure YYYY-MM-DD comparison
  // without timezone shifts, so holidays take precedence over
  // other reasons such as "weekend".
  const holidayInfo = getHolidayInfo({ date: candidate, holidays, targetTimezone });
  if (holidayInfo) {
    const title = holidayInfo.title || "Holiday";
    const description = holidayInfo.description || "";

    return description ? (
      <div>
        <p className="m-0 p-0">Title: {title}</p>
        <p className="m-0 p-0">Description: {description}</p>
      </div>
    ) : (
      title
    );
  }

  // Weekends (Saturday / Sunday)
  const dayOfWeek = candidate.day();
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return "Weekends are not allowed";
  }

  // Hard blackout dates: 28, 29, 30 Jan 2026 as pure calendar days
  const blackoutDates = ["2026-01-28", "2026-01-29", "2026-01-30"]; // YYYY-MM-DD
  const blackoutCheckStr = candidate.format("YYYY-MM-DD");
  if (blackoutDates.includes(blackoutCheckStr)) {
    return "Appointments are not available on this date";
  }

  // 30‑day future limit based on UTC window, but still driven
  // by the calendar day selected in the picker.
  const maxDateUtc = dayjs()
    .utc()
    .add(30, "day")
    .subtract(1, "day")
    .endOf("day");
  const utcEndOfDay = candidate.utc().endOf("day");
  if (utcEndOfDay.isAfter(maxDateUtc)) {
    return "Date cannot be more than 30 days in the future";
  }

  return "";
};
